import React, { useEffect, useState } from 'react'
import { cloneDeep } from 'lodash'
import { useRouter } from 'next/router'

import { ArchidektSearchMeta, generateArchidektSearchState } from 'types/searchV2'

import { useActionless, useAppSelector } from 'redux/hooks'
import { SET_COLLECTION_V2_STATE } from 'redux/collectionV2/actions/types'
import { collectionSpecificFiltersDefault } from 'redux/collectionV2/initialState'
import { CollectionSpecificFilters } from 'redux/collectionV2/types'

import Overlay, { OverlayExtras } from 'components/elements/Overlay'
import TabContainer from 'components/elements/TabButtons'
import ArchidektSearchForm from 'components/cardSearchPanel/searchFormElements/archidektSearchForm'
import ScryfallSearchForm from 'components/cardSearchPanel/searchFormElements/scryfallSearchForm'
import CollectionFilterOptions from './CollectionFilterOptions'

import { cardSearchStateToSearchParams, collectionSearchStateToSearchParams } from 'utils/Search'

import styles from './filterModal.module.scss'

type Props = {}

const CollectionFilterModal = ({}: Props) => {
  const router = useRouter()
  const username = `${router.query.username}`

  const filteringModalOpen = useAppSelector(state => state.collectionV2.filteringModalOpen)
  const filterType = useAppSelector(state => state.collectionV2.filterType)

  const [setCollectionV2State] = useActionless(SET_COLLECTION_V2_STATE)

  const handleClose = () => setCollectionV2State({ filteringModalOpen: false })

  const [archidektSearchState, setArchidektSearchState] = useState(generateArchidektSearchState())
  const [syntaxQuery, setSyntaxQuery] = useState('')
  const [collectionFilters, setCollectionFilters] = useState(cloneDeep(collectionSpecificFiltersDefault))

  const handleArchidektFilterSubmitted = (search: ArchidektSearchMeta) => handleFilters(search, '', collectionFilters)
  const handleSyntaxFilterSubmitted = (search: string) => handleFilters(null, search, collectionFilters)

  const handleFilters = (
    archidektSearch: ArchidektSearchMeta | null,
    scryfallSearch: string,
    collectionFilters: CollectionSpecificFilters,
  ) => {
    const collectionParams = collectionSearchStateToSearchParams(collectionFilters)
    const queryParams = archidektSearch
      ? `${cardSearchStateToSearchParams(archidektSearch)}&cardSearch=true`
      : `syntaxQuery=${scryfallSearch}`

    const orderParams = router.query.collectionOrderBy ? `&collectionOrderBy=${router.query.collectionOrderBy}` : ''
    const gameParams = router.query.game ? `&collectionGame=${router.query.game}` : ''

    let url = `/collection/v2/${username}/?${queryParams}`

    if (collectionParams) url += `&${collectionParams}`
    if (orderParams) url += orderParams
    if (gameParams) url += gameParams

    router.push(url)
  }

  useEffect(() => {
    if (filterType !== 'archidekt') setArchidektSearchState(generateArchidektSearchState())
    if (filterType !== 'scryfall') setSyntaxQuery('')
  }, [filterType])

  return (
    <Overlay open={filteringModalOpen} onClose={handleClose} className={styles.centerOverlay}>
      <OverlayExtras.BackButton onBack={handleClose} />

      <div className={styles.content}>
        <h1>Add collection filters</h1>

        <TabContainer
          className={styles.tabContainer}
          optionsClassName={styles.tabButton}
          options={[
            {
              label: 'Archidekt search',
              selected: filterType === 'archidekt',
              onClick: () => setCollectionV2State({ filterType: 'archidekt' }),
              className: filterType === 'archidekt' ? styles.activeTab : '',
            },
            {
              label: 'Syntax search',
              selected: filterType === 'scryfall',
              onClick: () => setCollectionV2State({ filterType: 'scryfall' }),
              className: filterType === 'scryfall' ? styles.activeTab : '',
            },
          ]}
        />

        {filterType === 'archidekt' && (
          <ArchidektSearchForm
            controlled
            searchOptions={archidektSearchState}
            onSubmitted={handleArchidektFilterSubmitted}
            onRequestFailed={() => console.error('failed')}
            hideTotal
            hideSort
            noAutoSearch
            className={styles.archidektSearchForm}
            containerClassName={styles.archidektSearchForm}
            placeholder="Search for cards to add to your collection"
            submitText="Filter collection"
          />
        )}

        {filterType === 'scryfall' && (
          <ScryfallSearchForm
            controlled
            searchString={syntaxQuery}
            onSubmitted={handleSyntaxFilterSubmitted}
            onRequestFailed={() => console.error('failed')}
            noAutoSearch
            className={styles.archidektSearchForm}
            containerClassName={styles.archidektSearchForm}
            placeholder="Search for cards to add to your collection"
            submitText="Filter collection"
            hideInfoLink
            hideTotal
          />
        )}

        <CollectionFilterOptions filters={collectionFilters} onChange={setCollectionFilters} />
      </div>
    </Overlay>
  )
}

export default CollectionFilterModal
